'use client';

import React, { useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── BRANCH / TRANSPLANT MECHANISM ───────────────────────────────────────────
// Allows caretakers to fork any letter section or concept into their garden
// as a seed, incubate it, and deploy it as a new tool or application.

interface BranchButtonProps {
  sourceType: 'letter_section' | 'concept' | 'name' | 'hadith' | 'ayah';
  sourceRef: string;
  sourceTitle: string;
  accentColor?: string;
  compact?: boolean;
}

const STATUS_LABELS = {
  seed: { label: '🌱 Seed', desc: 'Planted in your garden, not yet developed', color: 'rgba(45,122,71,0.7)' },
  incubating: { label: '🌿 Incubating', desc: 'Being developed into a tool or application', color: 'rgba(201,168,76,0.7)' },
  sprouting: { label: '🌳 Sprouting', desc: 'Taking shape — ready for community input', color: 'rgba(45,138,135,0.7)' },
  transplanted: { label: '✦ Transplanted', desc: 'Deployed as a standalone tool or application', color: 'rgba(138,138,184,0.8)' },
};

export default function BranchButton({ sourceType, sourceRef, sourceTitle, accentColor = 'rgba(45,122,71,0.7)', compact = false }: BranchButtonProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<'seed' | 'incubating' | 'sprouting' | 'transplanted'>('seed');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [branchId, setBranchId] = useState<string | null>(null);

  const saveBranch = useCallback(async () => {
    if (!title.trim()) return;
    setSaving(true);
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    const { data } = await supabase
      .from('branches')
      .insert({
        user_id: user.id,
        source_type: sourceType,
        source_ref: sourceRef,
        source_title: sourceTitle,
        title: title.trim(),
        body: body.trim() || null,
        status,
        is_public: isPublic,
        tags: [],
        names_extracted: [],
      })
      .select()
      .single();

    if (data) {
      setBranchId(data.id);
      setSaved(true);
      setTimeout(() => { setSaved(false); setOpen(false); }, 2500);
    }
    setSaving(false);
  }, [title, body, status, isPublic, sourceType, sourceRef, sourceTitle]);

  return (
    <div style={{ display: 'inline-block', position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: `1px solid ${accentColor}`,
          borderRadius: 4, padding: compact ? '3px 8px' : '5px 12px',
          cursor: 'pointer', color: accentColor,
          transition: 'all 0.15s',
        }}
        title={`Branch "${sourceTitle}" into your garden`}
      >
        <span style={{ fontSize: compact ? 10 : 12 }}>⑂</span>
        <span style={{ fontFamily: 'monospace', fontSize: compact ? 8 : 9, letterSpacing: '.1em', textTransform: 'uppercase' }}>
          Branch
        </span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 6,
          background: 'rgba(6,13,18,0.98)', border: '1px solid rgba(45,122,71,0.25)',
          borderRadius: 10, padding: '16px', width: 300,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          animation: 'brFadeIn 0.18s ease',
        }}>
          <style>{`@keyframes brFadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }`}</style>

          {/* Source */}
          <div style={{ marginBottom: 12, padding: '8px 10px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 6 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,200,100,0.5)', marginBottom: 3 }}>
              Branching from
            </p>
            <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.7)', lineHeight: 1.4 }}>
              {sourceTitle}
            </p>
          </div>

          {/* Title */}
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Name this branch / seed…"
            style={{
              width: '100%', background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
              padding: '7px 10px', color: 'rgba(212,197,169,0.8)',
              fontFamily: 'monospace', fontSize: 11, outline: 'none',
              marginBottom: 8, boxSizing: 'border-box',
            }}
          />

          {/* Body */}
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="What idea, tool, or application is this seed for? What do you want to incubate?"
            rows={3}
            style={{
              width: '100%', background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6,
              padding: '7px 10px', color: 'rgba(212,197,169,0.6)',
              fontFamily: 'monospace', fontSize: 10, outline: 'none',
              resize: 'vertical', marginBottom: 10, boxSizing: 'border-box',
            }}
          />

          {/* Status */}
          <div style={{ marginBottom: 10 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 6 }}>
              Initial status
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(Object.entries(STATUS_LABELS) as [string, typeof STATUS_LABELS[keyof typeof STATUS_LABELS]][]).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setStatus(key as any)}
                  style={{
                    fontFamily: 'monospace', fontSize: 8, padding: '3px 8px',
                    border: `1px solid ${status === key ? cfg.color : 'rgba(255,255,255,0.07)'}`,
                    background: status === key ? `${cfg.color}22` : 'transparent',
                    color: status === key ? cfg.color : 'rgba(212,197,169,0.35)',
                    borderRadius: 4, cursor: 'pointer',
                  }}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', marginTop: 4 }}>
              {STATUS_LABELS[status].desc}
            </p>
          </div>

          {/* Public toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
            <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} style={{ accentColor: '#2d7a47' }} />
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.45)' }}>
              Share branch publicly
            </span>
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setOpen(false)}
              style={{
                flex: 1, fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                padding: '6px', border: '1px solid rgba(255,255,255,0.07)', background: 'transparent',
                color: 'rgba(212,197,169,0.35)', borderRadius: 4, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={saveBranch}
              disabled={!title.trim() || saving}
              style={{
                flex: 2, fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '6px', border: `1px solid ${saved ? 'rgba(45,122,71,0.5)' : 'rgba(45,200,100,0.3)'}`,
                background: saved ? 'rgba(45,122,71,0.12)' : 'rgba(45,122,71,0.08)',
                color: saved ? 'rgba(45,200,100,0.9)' : 'rgba(45,200,100,0.7)',
                borderRadius: 4, cursor: !title.trim() || saving ? 'not-allowed' : 'pointer',
                opacity: !title.trim() ? 0.4 : 1,
              }}
            >
              {saving ? '…planting' : saved ? '✓ Planted in Garden' : '🌱 Plant Seed'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
